import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { MaterialIcon } from "@/components/MaterialIcon";
import { Badge, btnPrimary, btnSecondary, card } from "@/components/admin/ui";
import { money } from "@/lib/format";

const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = Math.ceil((new Date(iso) - new Date()) / 86400000);
  return diff <= 0 ? "today" : diff === 1 ? "in 1 day" : `in ${diff} days`;
};

const ConnectPanel = ({ connect, onClose, onLink, busy }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose} data-testid="payout-settings-backdrop">
    <div className={`${card} w-full max-w-[460px] p-6`} onClick={(e) => e.stopPropagation()} data-testid="payout-settings-panel">
      <p className="text-[17px] font-semibold">Payout account</p>
      <p className="mt-1 text-[13px] text-[#6B7280]">Dancerfloor pays out through Stripe. Link your bank account to receive your 85% share of every pass sold.</p>
      <div className="mt-5 space-y-2 text-[13px]">
        <div className="flex items-center justify-between">
          <span>Stripe account</span>
          <Badge tone={connect.connected ? "green" : "gray"}>{connect.connected ? "created" : "not linked"}</Badge>
        </div>
        <div className="flex items-center justify-between">
          <span>Payouts enabled</span>
          <Badge tone={connect.payouts_enabled ? "green" : "yellow"}>{connect.payouts_enabled ? "yes" : "pending"}</Badge>
        </div>
        {connect.requirements_due?.length > 0 && (
          <p className="text-[12px] text-[#92400E]" data-testid="payout-requirements">Stripe still needs: {connect.requirements_due.join(", ")}</p>
        )}
      </div>
      <div className="mt-6 flex items-center justify-end gap-2">
        <button type="button" className={btnSecondary} onClick={onClose} data-testid="payout-settings-close">Close</button>
        <button type="button" className={btnPrimary} onClick={onLink} disabled={busy} data-testid="link-bank-account-button">
          {busy ? "Opening Stripe…" : connect.connected ? "Manage on Stripe" : "Link bank account"}
        </button>
      </div>
    </div>
  </div>
);

export default function AdminPayouts() {
  const { data, reload } = useApi("/admin/payouts");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!data) return <p className="text-[14px] text-[#6B7280]">Loading…</p>;
  const { connect, is_admin: isAdmin } = data;

  const link = async () => {
    setBusy(true);
    try {
      const path = connect.connected && connect.details_submitted ? "/admin/connect/dashboard-link" : "/admin/connect/onboarding-link";
      const { data: res } = await api.post(path, { return_url: `${window.location.origin}/admin/payouts` });
      window.location.href = res.url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
      reload();
    }
  };

  return (
    <div data-testid="admin-payouts-page">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-[20px] md:text-[24px] font-semibold" data-testid="admin-page-title">Payouts</h1>
        {!isAdmin && (
          <button type="button" onClick={() => setOpen(true)} className="text-[#6B7280] transition-colors hover:text-black" aria-label="Payout settings" data-testid="payout-settings-button">
            <MaterialIcon name="settings" size={26} />
          </button>
        )}
      </div>
      {isAdmin && (
        <p className="-mt-3 mb-5 text-[13px] text-[#6B7280]" data-testid="payouts-admin-note">
          Your Dancerfloor share ({data.platform_fee_pct}% of every pass) across all festivals. Organizers receive their {100 - data.platform_fee_pct}% into their own linked Stripe accounts.
        </p>
      )}

      <div className="flex flex-col gap-6 md:flex-row md:items-center">
        <div className={`${card} w-full md:max-w-[300px] px-8 md:px-10 py-7 md:py-8 text-center`} data-testid="payout-balance-card">
          <p className="text-[30px] font-bold" data-testid="payout-balance">{money(data.balance, data.currency)}</p>
          <p className="mt-1 text-[14px] text-[#6B7280]">{isAdmin ? "Dancerfloor share" : "Account balance"}</p>
        </div>
        <div className="flex-1">
          <p className="text-[15px] font-semibold" data-testid="payout-next-line">
            Next payout will be {money(data.next_payout_amount, data.currency)}
            {data.next_payout_at ? ` ${daysUntil(data.next_payout_at)}` : ""}
          </p>
          <p className="mt-1.5 flex items-center gap-1.5 text-[14px] text-[#6B7280]" data-testid="payout-pending-line">
            {money(data.pending, data.currency)} is pending
            <span title={`Pass sales settle ${7} days after purchase, then move into your balance.`}>
              <MaterialIcon name="help" size={18} className="text-[#9CA3AF]" />
            </span>
          </p>
          {!connect.payouts_enabled && !isAdmin && (
            <button type="button" onClick={() => setOpen(true)} className="mt-3 text-[13px] font-semibold text-[#3B5BDB] hover:underline" data-testid="payout-connect-prompt">
              Link a bank account to get paid →
            </button>
          )}
        </div>
      </div>

      <div className="mt-10">
        <p className="mb-3 text-[15px] font-semibold">Payout history</p>
        {data.payouts.length === 0 ? (
          <p className="text-[15px] text-[#9CA3AF]" data-testid="payouts-empty">No payouts yet</p>
        ) : (
            <div className={`${card} overflow-hidden`} data-testid="payouts-table">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-[13px]">
              <thead className="bg-[#F9FAFB] text-left text-[12px] text-[#6B7280]">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Date</th>
                  <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {data.payouts.map((p) => (
                  <tr key={p.id} data-testid="payout-row">
                    <td className="px-4 py-2.5 text-[#6B7280]">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-medium">{money(p.amount, data.currency)}</td>
                    <td className="px-4 py-2.5"><Badge tone={p.status === "paid" ? "green" : "yellow"}>{p.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            </div>
        )}
        <p className="mt-4 text-[12px] text-[#6B7280]" data-testid="payout-fee-note">
          Dancerfloor keeps {data.platform_fee_pct}% of each pass. {isAdmin ? "Platform share" : "Lifetime earnings"}: {money(data.earnings, data.currency)}.
        </p>
      </div>

      {open && <ConnectPanel connect={connect} busy={busy} onClose={() => setOpen(false)} onLink={link} />}
    </div>
  );
}
